import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog.jsx";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MagnifyingGlass, Tag, UploadSimple, DownloadSimple, X, Sparkle, PencilSimple, ArrowSquareOut, Check, Prohibit, PaperPlaneTilt, MusicNotes, CaretLeft, CaretRight, Trash, Copy, TextAa } from "@phosphor-icons/react";
import { Frame } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";
import ImageEditor from "@/components/ImageEditor";
import BrandFrame from "@/components/BrandFrame";
import ImportFromUrls from "@/components/ImportFromUrls";
import GoogleDriveImport from "@/components/GoogleDriveImport";
import TemplateGallery from "@/components/TemplateGallery";
import { GoogleLogo } from "@phosphor-icons/react";

const STATUS_META = {
  pending: { label: "Needs review", bg: "bg-[#FEF3C7]", text: "text-[#92400E]" },
  rejected: { label: "Rejected", bg: "bg-[#FEE2E2]", text: "text-[#991B1B]" },
  approved: { label: "Approved", bg: "bg-[#D1FAE5]", text: "text-[#065F46]" },
};
const statusMeta = (item) => STATUS_META[item.review_status] || STATUS_META.approved;

export default function MediaLibrary() {
  const api = useApi();
  const navigate = useNavigate();
  const { canManageMedia, canDeleteMedia } = useRole();
  const [media, setMedia] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 60;
  const REVIEW_PAGE_SIZE = 60;
  const [pendingOffset, setPendingOffset] = useState(0);
  const [pendingHasMore, setPendingHasMore] = useState(false);
  const [pendingLoadingMore, setPendingLoadingMore] = useState(false);
  const [rejectedOffset, setRejectedOffset] = useState(0);
  const [rejectedHasMore, setRejectedHasMore] = useState(false);
  const [rejectedLoadingMore, setRejectedLoadingMore] = useState(false);
  const loadedTabs = useRef({ pending: false, rejected: false, all: false });
  const mediaFetchSeq = useRef(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadTags, setUploadTags] = useState("");
  const [editorItem, setEditorItem] = useState(null);
  const [frameItem, setFrameItem] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [driveOpen, setDriveOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState(null);
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "pending");
  const [pendingMedia, setPendingMedia] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [rejectedMedia, setRejectedMedia] = useState([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [reviewFeedback, setReviewFeedback] = useState({});
  const [actioningId, setActioningId] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { item, list }
  const [renameItem, setRenameItem] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [altTextValue, setAltTextValue] = useState("");
  const [assetDetails, setAssetDetails] = useState(null);
  const [kindFilter, setKindFilter] = useState(null); // null = all content types
  const fileInputRef = useRef(null);

  // Backend always knows the REAL extension (from the stored filename/mime); a display_name like
  // "Teshea Pitt - Move Better - Square Feed - V4" never has one. Without this, the <a download>
  // name had no extension and an untyped Blob — browsers then guess ".txt" as a fallback.
  const safeDownloadName = (item, contentDispositionHeader) => {
    const cdMatch = (contentDispositionHeader || "").match(/filename="([^"]+)"/);
    if (cdMatch) return cdMatch[1];
    const origExt = (item.filename || "").match(/\.([a-zA-Z0-9]{2,5})$/)?.[1];
    const ext = origExt || (item.kind === "video" ? "mp4" : item.kind === "audio" ? "mp3" : "png");
    const base = (item.display_name || item.filename || "download").replace(/\.[a-zA-Z0-9]{2,5}$/, "");
    return `${base}.${ext}`;
  };

  const handleDownload = async (item) => {
    try {
      const r = await api.get(`/media/${item.id}/download`, { responseType: "blob" });
      const contentType = r.headers?.["content-type"];
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], contentType ? { type: contentType } : undefined));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = safeDownloadName(item, r.headers?.["content-disposition"]);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed — the file may be missing from storage.");
    }
  };

  useEffect(() => {
    fetchCollections();
    // Load only the currently-active tab's data on mount — other tabs load on demand.
    if (tab === "pending") fetchPendingMedia(true);
    else if (tab === "rejected") fetchRejectedMedia(true);
    else fetchMedia(true);
    loadedTabs.current[tab] = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab !== "all") return;
    const t = setTimeout(() => fetchMedia(true), searchQuery ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeCollection, kindFilter]);

  useEffect(() => {
    if (tab !== "pending") return;
    const t = setTimeout(() => fetchPendingMedia(true), searchQuery ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (tab !== "rejected") return;
    const t = setTimeout(() => fetchRejectedMedia(true), searchQuery ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleTabChange = (next) => {
    setTab(next);
    if (loadedTabs.current[next]) return; // already fetched once — avoid duplicate requests
    loadedTabs.current[next] = true;
    if (next === "pending") fetchPendingMedia(true);
    else if (next === "rejected") fetchRejectedMedia(true);
    else fetchMedia(true);
  };

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") navigateLightbox(1);
      if (e.key === "ArrowLeft") navigateLightbox(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  const openLightbox = (item, list) => {
    setLightbox({ item, list });
    setAssetDetails(null);
    if (item.kind === "image") {
      api.get(`/media/${item.id}/details`).then((r) => setAssetDetails(r.data)).catch(() => {});
    }
  };
  const navigateLightbox = (dir) => {
    setLightbox((cur) => {
      if (!cur) return cur;
      const idx = cur.list.findIndex((m) => m.id === cur.item.id);
      const next = cur.list[idx + dir];
      if (next) {
        setAssetDetails(null);
        if (next.kind === "image") api.get(`/media/${next.id}/details`).then((r) => setAssetDetails(r.data)).catch(() => {});
      }
      return next ? { item: next, list: cur.list } : cur;
    });
  };

  const fetchCollections = async () => {
    try {
      const r = await api.get(`/media/collections`);
      setCollections(r.data || []);
    } catch (e) { /* non-blocking */ }
  };

  const fetchMedia = async (reset = true) => {
    const seq = ++mediaFetchSeq.current;
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const nextOffset = reset ? 0 : offset;
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(nextOffset) });
      if (searchQuery.trim()) params.set("q", searchQuery.trim());
      if (activeCollection) params.set("collection", activeCollection);
      if (kindFilter) params.set("kind", kindFilter);
      const response = await api.get(`/media?${params.toString()}`);
      if (seq !== mediaFetchSeq.current) return; // a newer request already superseded this one
      const items = response.data || [];
      setMedia((prev) => (reset ? items : [...prev, ...items]));
      setOffset(nextOffset + items.length);
      setHasMore(items.length === PAGE_SIZE);
    } catch (error) {
      if (seq === mediaFetchSeq.current) toast.error("Failed to fetch media");
    } finally {
      if (seq === mediaFetchSeq.current) { setLoading(false); setLoadingMore(false); }
    }
  };

  const fetchPendingMedia = async (reset = true) => {
    if (reset) setPendingLoading(true); else setPendingLoadingMore(true);
    try {
      const nextOffset = reset ? 0 : pendingOffset;
      const params = new URLSearchParams({ review_status: "pending", limit: String(REVIEW_PAGE_SIZE), offset: String(nextOffset) });
      if (searchQuery.trim()) params.set("q", searchQuery.trim());
      const response = await api.get(`/media?${params.toString()}`);
      const items = response.data || [];
      setPendingMedia((prev) => (reset ? items : [...prev, ...items]));
      setPendingOffset(nextOffset + items.length);
      setPendingHasMore(items.length === REVIEW_PAGE_SIZE);
    } catch (error) {
      toast.error("Failed to fetch pending review items");
    } finally {
      setPendingLoading(false);
      setPendingLoadingMore(false);
    }
  };

  const fetchRejectedMedia = async (reset = true) => {
    if (reset) setRejectedLoading(true); else setRejectedLoadingMore(true);
    try {
      const nextOffset = reset ? 0 : rejectedOffset;
      const params = new URLSearchParams({ review_status: "rejected", limit: String(REVIEW_PAGE_SIZE), offset: String(nextOffset) });
      if (searchQuery.trim()) params.set("q", searchQuery.trim());
      const response = await api.get(`/media?${params.toString()}`);
      const items = response.data || [];
      setRejectedMedia((prev) => (reset ? items : [...prev, ...items]));
      setRejectedOffset(nextOffset + items.length);
      setRejectedHasMore(items.length === REVIEW_PAGE_SIZE);
    } catch (error) {
      toast.error("Failed to fetch rejected items");
    } finally {
      setRejectedLoading(false);
      setRejectedLoadingMore(false);
    }
  };

  const handleApprove = async (id) => {
    setActioningId(id);
    try {
      await api.post(`/media/${id}/approve`, reviewFeedback[id] ? { feedback: reviewFeedback[id] } : {});
      toast.success("Approved — now usable in campaigns");
      setPendingMedia((prev) => prev.map((m) => (m.id === id ? { ...m, review_status: "approved" } : m)));
      setReviewFeedback((f) => { const c = { ...f }; delete c[id]; return c; });
      setLightbox((cur) => (cur && cur.item.id === id ? null : cur));
      fetchMedia();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setActioningId(null); }
  };
  const handleUseInPost = (item) => {
    navigate("/calendar", { state: { attachMediaId: item.id } });
  };

  const handleReject = async (id) => {
    setActioningId(id);
    try {
      await api.post(`/media/${id}/reject`, reviewFeedback[id] ? { feedback: reviewFeedback[id] } : {});
      toast.success("Rejected");
      setPendingMedia((prev) => prev.filter((m) => m.id !== id));
      setReviewFeedback((f) => { const c = { ...f }; delete c[id]; return c; });
      setLightbox((cur) => (cur && cur.item.id === id ? null : cur));
      fetchRejectedMedia();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setActioningId(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image permanently? This can't be undone.")) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success("Image deleted");
      setMedia((prev) => prev.filter((m) => m.id !== id));
      setRejectedMedia((prev) => prev.filter((m) => m.id !== id));
      setLightbox((cur) => (cur && cur.item.id === id ? null : cur));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const openRename = (item) => { setRenameItem(item); setRenameValue(item.display_name || item.filename); setAltTextValue(item.alt_text || ""); };
  const submitRename = async () => {
    if (!renameValue.trim()) { toast.error("Name can't be empty"); return; }
    try {
      const r = await api.patch(`/media/${renameItem.id}/rename`, { display_name: renameValue.trim(), alt_text: altTextValue.trim() });
      setMedia((prev) => prev.map((m) => (m.id === r.data.id ? { ...m, ...r.data } : m)));
      setLightbox((cur) => (cur && cur.item.id === r.data.id ? { ...cur, item: { ...cur.item, ...r.data } } : cur));
      toast.success("Saved");
      setRenameItem(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const handleCopyPublicUrl = async (item) => {
    try {
      const r = await api.post(`/media/${item.id}/publish`);
      await navigator.clipboard.writeText(r.data.email_url);
      setMedia((prev) => prev.map((m) => (m.id === item.id ? { ...m, is_public: true, public_url: r.data.public_url, email_url: r.data.email_url } : m)));
      toast.success("Email URL copied — the fast-loading, optimized version, safe for newsletters, Mailchimp, or Claude");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not get an email URL");
    }
  };


  const handleEditSaved = (newMedia) => {
    const wasRejected = editorItem?.review_status === "rejected";
    setEditorItem(null);
    if (wasRejected) {
      toast.success("Updated version resubmitted for review");
      setRejectedMedia((prev) => prev.filter((m) => m.id !== editorItem.id));
      setTab("pending");
      fetchPendingMedia();
    } else {
      fetchMedia();
    }
  };

  const handleFileSelect = (e) => setSelectedFiles(Array.from(e.target.files));
  const handleRemoveFile = (index) => setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

  const handleUpload = async () => {
    if (selectedFiles.length === 0) { toast.error("Please select files to upload"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append("files", file));
      const response = await api.post(
        `/media/upload${uploadTags ? `?tags=${encodeURIComponent(uploadTags)}` : ""}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const { uploaded, errors } = response.data;
      if (uploaded > 0) {
        toast.success(`Uploaded ${uploaded} file(s) — pending review before use`);
      }
      if (errors && errors.length > 0) {
        errors.forEach((e) => toast.error(`${e.filename}: ${e.error}`));
      }
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setUploadTags("");
      fetchMedia();
      fetchPendingMedia();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.errors?.length) {
        detail.errors.forEach((e) => toast.error(`${e.filename}: ${e.error}`));
      } else {
        toast.error(detail?.message || detail || "Upload failed. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  };

  const isAiGenerated = (item) => item.tags?.some((t) => ["ai-generated", "ai", "dalle", "gpt-image", "nano-banana"].includes(t.toLowerCase()));

  const pendingCount = pendingMedia.filter((m) => m.review_status !== "approved").length;

  return (
    <div className="space-y-8 bg-[#F8FAF9] -m-6 p-6 min-h-[calc(100vh-4rem)]" data-testid="media-library-container">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#2D3139] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>
            Media Library
          </h1>
          <p className="text-[#6B7C85]">Browse and manage your workspace media assets</p>
        </div>
        <div className={`flex items-center gap-3 flex-wrap ${canManageMedia ? "" : "hidden"}`}>
          <Button data-testid="import-urls-button" onClick={() => setImportOpen(true)} className="bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] transition-colors duration-200 rounded-[12px]">
            <ArrowSquareOut size={18} className="mr-2" weight="regular" />
            Paste image links
          </Button>
          <Button data-testid="drive-import-open-button" onClick={() => setDriveOpen(true)} className="bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] transition-colors duration-200 rounded-[12px]">
            <GoogleLogo size={18} className="mr-2" weight="regular" />
            Google Drive
          </Button>
          <Button data-testid="upload-media-button" onClick={() => setUploadDialogOpen(true)} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] transition-colors duration-200 rounded-[12px]">
            <UploadSimple size={18} className="mr-2" weight="regular" />
            Upload Media
          </Button>
        </div>
      </div>

      {canManageMedia && (
        <div className="flex items-center gap-2" data-testid="media-tabs">
          {[
            { k: "pending", label: `Pending Review${pendingCount ? ` (${pendingCount})` : ""}` },
            { k: "all", label: "Approved & All Media" },
            { k: "rejected", label: `Rejected${rejectedMedia.length ? ` (${rejectedMedia.length})` : ""}` },
            { k: "templates", label: "Newsletter Templates" },
          ].map((t) => (
            <button key={t.k} onClick={() => handleTabChange(t.k)} data-testid={`media-tab-${t.k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${tab === t.k ? "bg-[#3A7A8C] text-white border-[#3A7A8C]" : "bg-white text-[#2D3139] border-[#E2E8F0] hover:border-[#5FA8B4]"}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {tab === "pending" ? (
        <div data-testid="pending-review-panel">
          <p className="text-[#6B7C85] text-sm mb-4">Every new upload — and every photo found automatically by Shee or imported from social — lands here first. Nothing here can be used in a post until it's approved.</p>
          <div className="relative mb-4 max-w-md">
            <MagnifyingGlass size={20} weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7C85]" />
            <Input
              data-testid="pending-search-input"
              type="text"
              placeholder="Search by name, campaign, or tag…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#F8FAF9] border-[#E2E8F0] text-[#2D3139] placeholder:text-[#9AA6AC] focus:border-[#5FA8B4]"
            />
          </div>
          {pendingLoading ? (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center"><p className="text-[#6B7C85]">Loading…</p></div>
          ) : pendingMedia.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center">
              <p className="text-[#6B7C85]">Nothing waiting for review.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {pendingMedia.map((item) => {
                const meta = statusMeta(item);
                return (
                  <div key={item.id} data-testid={`pending-item-${item.id}`} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden hover:shadow-lg hover:border-[#5FA8B4] transition-all duration-200 group flex flex-col">
                    <div className="aspect-video bg-slate-100 overflow-hidden relative cursor-pointer" onClick={() => openLightbox(item, pendingMedia)}>
                      {(item.thumbnail_url || item.file_url) ? (
                        <img src={item.thumbnail_url || item.file_url} loading="lazy" alt={item.display_name || item.filename} className="w-full h-full object-cover"
                          onError={(e) => { if (item.thumbnail_url && e.target.src !== item.file_url) e.target.src = item.file_url; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#9AA6AC] text-xs">No preview</div>
                      )}
                      <span className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full ${meta.bg} ${meta.text} text-[10px] font-semibold`} data-testid={`pending-badge-${item.id}`}>
                        {meta.label}
                      </span>
                      {item.source_platform && (
                        <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#2D3139] text-white text-[10px] font-semibold capitalize" data-testid={`source-badge-${item.id}`}>
                          {item.source_platform}
                        </span>
                      )}
                    </div>
                    <div className="p-5 space-y-3">
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-[#2D3139] truncate" title={item.display_name || item.filename} data-testid={`pending-filename-${item.id}`}>{item.display_name || item.filename}</p>
                          <button data-testid={`pending-download-${item.id}`} onClick={() => handleDownload(item)} title="Download" className="text-[#6B7C85] hover:text-[#3A7A8C] flex-shrink-0">
                            <DownloadSimple size={16} weight="regular" />
                          </button>
                        </div>
                        <p className="text-[#6B7C85] text-xs mt-1">Found: {new Date(item.uploaded_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</p>
                      </div>
                      {item.review_status === "approved" ? (
                        <Button data-testid={`use-in-post-${item.id}`} onClick={() => handleUseInPost(item)} className="w-full bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] h-8 text-xs rounded-[12px] gap-1">
                          <PaperPlaneTilt size={14} weight="fill" /> Use in a post
                        </Button>
                      ) : (
                        <>
                          <Textarea data-testid={`review-feedback-${item.id}`} placeholder="Optional comment for the team…" value={reviewFeedback[item.id] || ""}
                            onChange={(e) => setReviewFeedback((f) => ({ ...f, [item.id]: e.target.value }))}
                            className="bg-[#F8FAF9] border-[#E2E8F0] text-[#2D3139] text-xs min-h-[52px] resize-none" />
                          <div className="flex gap-2">
                            <Button data-testid={`approve-media-${item.id}`} disabled={actioningId === item.id} onClick={() => handleApprove(item.id)} className="flex-1 bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] h-8 text-xs rounded-[12px] gap-1">
                              <Check size={14} weight="bold" /> Approve
                            </Button>
                            <Button data-testid={`reject-media-${item.id}`} disabled={actioningId === item.id} onClick={() => handleReject(item.id)} className="flex-1 bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white h-8 text-xs rounded-[12px] gap-1">
                              <Prohibit size={14} /> Reject
                            </Button>
                            {canDeleteMedia && (
                              <Button data-testid={`delete-pending-${item.id}`} onClick={() => handleDelete(item.id)} className="bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white h-8 text-xs rounded-[12px] px-2.5">
                                <Trash size={14} />
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {pendingHasMore && (
            <div className="flex justify-center pt-4">
              <Button data-testid="pending-load-more-button" onClick={() => fetchPendingMedia(false)} disabled={pendingLoadingMore}
                className="bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] rounded-[12px] px-6">
                {pendingLoadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </div>
      ) : tab === "rejected" ? (
        <div data-testid="rejected-review-panel">
          <p className="text-[#6B7C85] text-sm mb-4">Items an Admin/Editor has rejected, with any comment left for the team. Never used in posts.</p>
          <div className="relative mb-4 max-w-md">
            <MagnifyingGlass size={20} weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7C85]" />
            <Input
              data-testid="rejected-search-input"
              type="text"
              placeholder="Search by name, campaign, or tag…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#F8FAF9] border-[#E2E8F0] text-[#2D3139] placeholder:text-[#9AA6AC] focus:border-[#5FA8B4]"
            />
          </div>
          {rejectedLoading ? (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center"><p className="text-[#6B7C85]">Loading…</p></div>
          ) : rejectedMedia.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center">
              <p className="text-[#6B7C85]">Nothing rejected.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {rejectedMedia.map((item) => (
                <div key={item.id} data-testid={`rejected-item-${item.id}`} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden group">
                  <div className="aspect-video bg-slate-100 overflow-hidden relative cursor-pointer" onClick={() => openLightbox(item, rejectedMedia)}>
                    {(item.thumbnail_url || item.file_url) ? (
                      <img src={item.thumbnail_url || item.file_url} loading="lazy" alt={item.display_name || item.filename} className="w-full h-full object-cover opacity-60"
                        onError={(e) => { if (item.thumbnail_url && e.target.src !== item.file_url) e.target.src = item.file_url; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#9AA6AC] text-xs">No preview</div>
                    )}
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FEE2E2] text-[#991B1B] text-[10px] font-semibold">
                      Rejected
                    </span>
                  </div>
                  <div className="p-5 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-[#2D3139] truncate" title={item.display_name || item.filename} data-testid={`rejected-filename-${item.id}`}>{item.display_name || item.filename}</p>
                      <button data-testid={`rejected-download-${item.id}`} onClick={() => handleDownload(item)} title="Download" className="text-[#6B7C85] hover:text-[#3A7A8C] flex-shrink-0">
                        <DownloadSimple size={16} weight="regular" />
                      </button>
                    </div>
                    <p className="text-[#6B7C85] text-xs">Uploaded: {new Date(item.uploaded_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</p>
                    {item.review_feedback ? (
                      <p className="text-[#991b1b] text-xs bg-[#fef2f2] border border-[#fecaca] rounded-[8px] p-2" data-testid={`rejected-feedback-${item.id}`}>{item.review_feedback}</p>
                    ) : (
                      <p className="text-[#6B7C85] text-xs italic">No comment left.</p>
                    )}
                    {canManageMedia && (
                      <div className="flex gap-2 pt-1">
                        <Button data-testid={`edit-rejected-${item.id}`} onClick={() => { setLightbox(null); setEditorItem(item); }} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-8 text-xs rounded-[12px] gap-1">
                          <PencilSimple size={14} weight="regular" /> Edit & Resubmit
                        </Button>
                        {canDeleteMedia && (
                          <Button data-testid={`delete-rejected-${item.id}`} onClick={() => handleDelete(item.id)} className="bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white h-8 text-xs rounded-[12px] px-3">
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {rejectedHasMore && (
            <div className="flex justify-center pt-4">
              <Button data-testid="rejected-load-more-button" onClick={() => fetchRejectedMedia(false)} disabled={rejectedLoadingMore}
                className="bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] rounded-[12px] px-6">
                {rejectedLoadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </div>
      ) : tab === "templates" ? (
        <TemplateGallery />
      ) : (
      <>
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4">
        <div className="relative">
          <MagnifyingGlass size={20} weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7C85]" />
          <Input
            data-testid="media-search-input"
            type="text"
            placeholder="Search by filename or tags…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#F8FAF9] border-[#E2E8F0] text-[#2D3139] placeholder:text-[#9AA6AC] focus:border-[#5FA8B4]"
          />
        </div>
        {collections.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="collection-filters">
            <span className="text-xs text-[#6B7C85] mr-1">Collections:</span>
            <button
              onClick={() => setActiveCollection(null)}
              data-testid="collection-chip-all"
              className={`text-xs px-3 py-1 rounded-full transition-colors ${!activeCollection ? "bg-[#5FA8B4] text-white" : "bg-[#F0F7F8] text-[#3A7A8C] hover:bg-[#E6F2F4]"}`}
            >All</button>
            {collections.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCollection(c)}
                data-testid={`collection-chip-${c}`}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${activeCollection === c ? "bg-[#5FA8B4] text-white" : "bg-[#F0F7F8] text-[#3A7A8C] hover:bg-[#E6F2F4]"}`}
              >{c}</button>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="kind-filters">
          <span className="text-xs text-[#6B7C85] mr-1">Type:</span>
          {[{ k: null, label: "All" }, { k: "image", label: "Images" }, { k: "video", label: "Videos" },
            { k: "audio", label: "Audio" }, { k: "document", label: "Documents" }].map((f) => (
            <button
              key={f.label}
              onClick={() => setKindFilter(f.k)}
              data-testid={`kind-chip-${f.label.toLowerCase()}`}
              className={`text-xs px-3 py-1 rounded-full transition-colors ${kindFilter === f.k ? "bg-[#3A7A8C] text-white" : "bg-[#F0F7F8] text-[#3A7A8C] hover:bg-[#E6F2F4]"}`}
            >{f.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center" data-testid="media-library-loading"><p className="text-[#6B7C85]">Loading media library…</p></div>
      ) : media.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center">
          <p className="text-[#6B7C85]">{searchQuery ? "No media found matching your search" : "No media uploaded yet"}</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {media.map((item) => (
            <div key={item.id} data-testid={`media-item-${item.id}`} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden hover:shadow-lg hover:border-[#5FA8B4] transition-all duration-200 group flex flex-col">
              <div className="aspect-video bg-slate-100 overflow-hidden relative cursor-pointer" onClick={() => item.kind === "image" && openLightbox(item, media)}>
                {item.kind === "audio" ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#eef1f2] p-3" data-testid={`audio-item-${item.id}`}>
                    <MusicNotes size={28} className="text-[#3A7A8C]" weight="fill" />
                    {item.file_url ? (
                      <audio controls src={item.file_url} className="w-full" onClick={(e) => e.stopPropagation()} />
                    ) : (
                      <span className="text-[#9AA6AC] text-xs">No preview</span>
                    )}
                  </div>
                ) : item.kind === "video" ? (
                  item.file_url ? (
                    <video src={item.file_url} controls className="w-full h-full object-cover" data-testid={`video-item-${item.id}`} onClick={(e) => e.stopPropagation()} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#9AA6AC] text-xs" data-testid={`video-item-${item.id}`}>No preview</div>
                  )
                ) : (item.thumbnail_url || item.file_url) ? (
                  <img src={item.thumbnail_url || item.file_url} loading="lazy" alt={item.display_name || item.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    onError={(e) => { if (item.thumbnail_url && e.target.src !== item.file_url) e.target.src = item.file_url; }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#9AA6AC] text-xs">No preview</div>
                )}
                {isAiGenerated(item) && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#3A7A8C] text-white text-[10px] font-semibold" data-testid={`ai-badge-${item.id}`}>
                    <Sparkle size={11} weight="fill" /> AI-generated
                  </span>
                )}
                {item.tags?.includes("edited") && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#5FA8B4] text-white text-[10px] font-semibold" data-testid={`edited-badge-${item.id}`}>
                    Edited
                  </span>
                )}
                {item.tags?.includes("branded") && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#3A7A8C] text-white text-[10px] font-semibold" data-testid={`branded-badge-${item.id}`}>
                    Branded
                  </span>
                )}
                {item.is_public && (
                  <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 text-[#3A7A8C] text-[10px] font-semibold" data-testid={`public-badge-${item.id}`}>
                    {item.health_status || "Public"}
                  </span>
                )}
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-[#2D3139] truncate" title={item.display_name || item.filename} data-testid={`media-filename-${item.id}`}>{item.display_name || item.filename}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {item.tags.map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-[#eef1f2] text-[#3A7A8C] text-xs rounded">
                        <Tag size={10} weight="regular" />
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="text-[#6B7C85] text-xs mt-2">Uploaded: {new Date(item.uploaded_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button data-testid={`download-media-${item.id}`} onClick={() => handleDownload(item)} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-8 text-xs rounded-[12px] gap-1">
                    <DownloadSimple size={14} weight="regular" /> Download
                  </Button>
                  {canManageMedia && item.kind !== "audio" && (
                    <Button data-testid={`edit-media-${item.id}`} onClick={() => setEditorItem(item)} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-8 text-xs rounded-[12px] gap-1">
                      <PencilSimple size={14} weight="regular" /> Edit
                    </Button>
                  )}
                  {canManageMedia && item.kind !== "audio" && (
                    <Button data-testid={`frame-media-${item.id}`} onClick={() => setFrameItem(item)} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-8 text-xs rounded-[12px] gap-1">
                      <Frame size={14} /> Frame
                    </Button>
                  )}
                  {canManageMedia && item.kind === "image" && (
                    <Button data-testid={`rename-media-${item.id}`} onClick={() => openRename(item)} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-8 text-xs rounded-[12px] gap-1">
                      <TextAa size={14} weight="regular" /> Rename
                    </Button>
                  )}
                  {item.kind === "image" && (
                    <Button data-testid={`copy-public-url-${item.id}`} onClick={() => handleCopyPublicUrl(item)} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-8 text-xs rounded-[12px] gap-1">
                      <Copy size={14} weight="regular" /> Copy Email URL
                    </Button>
                  )}
                  {canDeleteMedia && (
                    <Button data-testid={`delete-media-${item.id}`} onClick={() => handleDelete(item.id)} className="bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white h-8 text-xs rounded-[12px] px-3">
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        {hasMore && (
          <div className="flex justify-center pt-2">
            <Button data-testid="media-load-more-button" onClick={() => fetchMedia(false)} disabled={loadingMore}
              className="bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] rounded-[12px] px-6">
              {loadingMore ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
        </>
      )}
      </>
      )}

      <Dialog open={!!renameItem} onOpenChange={(o) => !o && setRenameItem(null)}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#2D3139] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Rename</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Display name</Label>
              <Input data-testid="rename-media-input" value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitRename()} className="bg-[#F8FAF9] border-[#E2E8F0] text-[#2D3139] mt-1" autoFocus />
            </div>
            <div>
              <Label>Alt text <span className="text-[#6B7C85] font-normal">(used for newsletter accessibility &amp; SEO)</span></Label>
              <Input data-testid="rename-media-alt-text-input" value={altTextValue} onChange={(e) => setAltTextValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitRename()} placeholder="e.g. Smiling patient in the waiting room" className="bg-[#F8FAF9] border-[#E2E8F0] text-[#2D3139] mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button data-testid="rename-media-cancel" onClick={() => setRenameItem(null)} className="bg-white border border-[#E2E8F0] text-[#2D3139] hover:bg-[#F8FAF9]">Cancel</Button>
              <Button data-testid="rename-media-save" onClick={submitRename} className="bg-[#3A7A8C] text-white hover:bg-[#2f6575]">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#2D3139] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Upload Media</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div>
              <Label className="text-[#2D3139] mb-2 block">Select Images</Label>
              <input ref={fileInputRef} type="file" multiple accept="image/*,video/mp4,video/quicktime,video/webm" onChange={handleFileSelect} className="hidden" />
              <Button data-testid="select-files-button" onClick={() => fileInputRef.current?.click()} className="w-full bg-[#F8FAF9] border border-[#E2E8F0] text-[#2D3139] hover:bg-[#F0F7F8] hover:border-[#5FA8B4] transition-colors duration-200 rounded-[12px]">
                <UploadSimple size={18} className="mr-2" weight="regular" />
                Select Files
              </Button>
            </div>

            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <Label className="text-[#2D3139]">Selected Files ({selectedFiles.length})</Label>
                <div className="bg-[#F8FAF9] border border-[#E2E8F0] rounded-[12px] p-4 max-h-48 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-[#E2E8F0] last:border-0">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-[#eef1f2] rounded flex items-center justify-center flex-shrink-0">
                          {file.type.startsWith("image/") && (
                            <img src={URL.createObjectURL(file)} alt={file.name} className="w-full h-full object-cover rounded" />
                          )}
                          {file.type.startsWith("video/") && (
                            <video src={URL.createObjectURL(file)} className="w-full h-full object-cover rounded" data-testid={`selected-video-preview-${index}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#2D3139] text-sm truncate">{file.name}</p>
                          <p className="text-[#6B7C85] text-xs">{(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <button onClick={() => handleRemoveFile(index)} className="text-[#6B7C85] hover:text-[#2D3139] transition-colors duration-200 ml-2">
                        <X size={18} weight="regular" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="upload-tags" className="text-[#2D3139] mb-2 block">Tags (comma-separated)</Label>
              <Input id="upload-tags" data-testid="upload-tags-input" type="text" placeholder="e.g., clinic, interior, modern" value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} className="bg-[#F8FAF9] border-[#E2E8F0] text-[#2D3139] placeholder:text-[#9AA6AC] focus:border-[#5FA8B4]" />
              <p className="text-[#6B7C85] text-xs mt-1">Tags help organize and search your media assets</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button data-testid="upload-submit-button" onClick={handleUpload} disabled={uploading || selectedFiles.length === 0} className="flex-1 bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] transition-colors duration-200 rounded-[12px]">
                {uploading ? "Uploading…" : `Upload ${selectedFiles.length} file(s)`}
              </Button>
              <Button onClick={() => { setUploadDialogOpen(false); setSelectedFiles([]); setUploadTags(""); }} disabled={uploading} className="bg-white border border-[#E2E8F0] text-[#2D3139] hover:bg-[#f5f5f5] rounded-[12px]">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {editorItem && (
        <ImageEditor
          open={!!editorItem}
          onOpenChange={(v) => !v && setEditorItem(null)}
          src={editorItem.file_url}
          filename={editorItem.filename}
          resubmitOf={editorItem.review_status === "rejected" ? editorItem.id : undefined}
          onSaved={handleEditSaved}
        />
      )}

      {frameItem && (
        <BrandFrame
          open={!!frameItem}
          onOpenChange={(v) => !v && setFrameItem(null)}
          src={frameItem.file_url}
          filename={frameItem.filename}
          onSaved={() => { setFrameItem(null); fetchMedia(); }}
        />
      )}

      <ImportFromUrls open={importOpen} onOpenChange={setImportOpen} onImported={() => { fetchMedia(); fetchCollections(); fetchPendingMedia(); }} />
      <GoogleDriveImport open={driveOpen} onOpenChange={setDriveOpen} onImported={() => { fetchMedia(); fetchCollections(); fetchPendingMedia(); setTab("pending"); }} />

      {lightbox && (() => {
        const item = lightbox.item;
        const meta = statusMeta(item);
        const idx = lightbox.list.findIndex((m) => m.id === item.id);
        return (
          <div
            className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-50 flex items-center justify-center p-4 sm:p-6"
            data-testid="media-lightbox-modal"
            onClick={() => setLightbox(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden max-w-7xl w-[94vw] h-[88vh] flex flex-col md:flex-row relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button data-testid="media-lightbox-close-button" onClick={() => setLightbox(null)}
                className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white text-[#2D3139] rounded-full p-2 shadow">
                <X size={18} weight="bold" />
              </button>
              <div className="flex-1 bg-slate-950 flex items-center justify-center p-4 min-h-[300px] md:min-h-0 relative overflow-hidden">
                {idx > 0 && (
                  <button data-testid="media-lightbox-prev-button" onClick={() => navigateLightbox(-1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#2D3139] rounded-full p-2 shadow">
                    <CaretLeft size={18} weight="bold" />
                  </button>
                )}
                {idx < lightbox.list.length - 1 && (
                  <button data-testid="media-lightbox-next-button" onClick={() => navigateLightbox(1)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#2D3139] rounded-full p-2 shadow">
                    <CaretRight size={18} weight="bold" />
                  </button>
                )}
                {item.kind === "video" ? (
                  item.file_url ? (
                    <video src={item.file_url} controls autoPlay className="max-h-full w-auto max-w-full object-contain rounded-lg shadow-md" data-testid="media-lightbox-image" />
                  ) : (
                    <div className="text-slate-400 text-sm" data-testid="media-lightbox-image">No preview available</div>
                  )
                ) : item.file_url ? (
                  <img src={item.file_url} alt={item.alt_text || item.display_name || item.filename} className="max-h-full w-auto max-w-full object-contain rounded-lg shadow-md" data-testid="media-lightbox-image" />
                ) : (
                  <div className="text-slate-400 text-sm" data-testid="media-lightbox-image">No preview available</div>
                )}
              </div>
              <div className="w-full md:w-80 lg:w-96 bg-slate-50 border-t md:border-t-0 md:border-l border-slate-200 p-6 flex flex-col justify-between overflow-y-auto">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-[#2D3139] break-words" data-testid="media-lightbox-filename">{item.display_name || item.filename}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${meta.bg} ${meta.text} text-[10px] font-semibold`}>{meta.label}</span>
                      {item.kind === "image" && (
                        <span data-testid="media-lightbox-health-status" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-[#E2E8F0] text-[#3A7A8C] text-[10px] font-semibold">
                          {assetDetails ? assetDetails.health_status : (item.is_public ? "Public" : "Private")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-[#4A5568] space-y-1">
                    {item.source_platform && <p>Source: <span className="capitalize font-medium">{item.source_platform}</span></p>}
                    <p>Uploaded: {new Date(item.uploaded_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</p>
                    {item.kind === "image" && (
                      <p data-testid="media-lightbox-dimensions">
                        {assetDetails?.width ? `${assetDetails.width} × ${assetDetails.height}px` : "…"}
                        {assetDetails?.file_size ? ` · ${(assetDetails.file_size / 1024).toFixed(0)} KB` : ""}
                        {assetDetails?.content_type ? ` · ${assetDetails.content_type}` : ""}
                      </p>
                    )}
                    {item.alt_text && <p className="italic text-[#6B7C85]">Alt text: "{item.alt_text}"</p>}
                  </div>
                  {item.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {item.tags.map((tag) => (
                        <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-[#eef1f2] text-[#3A7A8C] text-xs rounded">
                          <Tag size={10} weight="regular" />{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {item.review_status === "pending" && (
                    <div className="space-y-2 pt-2 border-t border-slate-200">
                      <Label className="text-xs text-[#4A5568]">Comment for the team</Label>
                      <Textarea data-testid="media-lightbox-feedback" placeholder="Optional comment…" value={reviewFeedback[item.id] || ""}
                        onChange={(e) => setReviewFeedback((f) => ({ ...f, [item.id]: e.target.value }))}
                        className="bg-white border-[#E2E8F0] text-[#2D3139] text-xs min-h-[60px] resize-none" />
                    </div>
                  )}
                  {item.review_status === "rejected" && (
                    item.review_feedback ? (
                      <p className="text-[#991b1b] text-xs bg-[#fef2f2] border border-[#fecaca] rounded-[8px] p-2">{item.review_feedback}</p>
                    ) : (
                      <p className="text-[#6B7C85] text-xs italic">No comment left.</p>
                    )
                  )}
                </div>

                <div className="space-y-2 pt-4">
                  <Button data-testid="media-lightbox-download-button" onClick={() => handleDownload(item)} className="w-full bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-9 text-xs rounded-[12px] gap-1">
                    <DownloadSimple size={14} weight="regular" /> Download full resolution
                  </Button>
                  {item.review_status === "pending" ? (
                    <div className="flex gap-2">
                      <Button data-testid="media-lightbox-approve-button" disabled={actioningId === item.id} onClick={() => handleApprove(item.id)} className="flex-1 bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] h-9 text-xs rounded-[12px] gap-1">
                        <Check size={14} weight="bold" /> Approve
                      </Button>
                      <Button data-testid="media-lightbox-reject-button" disabled={actioningId === item.id} onClick={() => handleReject(item.id)} className="flex-1 bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white h-9 text-xs rounded-[12px] gap-1">
                        <Prohibit size={14} /> Reject
                      </Button>
                      {canDeleteMedia && (
                        <Button data-testid="media-lightbox-delete-pending-button" onClick={() => handleDelete(item.id)} className="bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white h-9 text-xs rounded-[12px] px-3">
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  ) : item.review_status !== "rejected" ? (
                    <>
                      <Button data-testid="media-lightbox-use-in-post-button" onClick={() => { setLightbox(null); handleUseInPost(item); }} className="w-full bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] h-9 text-xs rounded-[12px] gap-1">
                        <PaperPlaneTilt size={14} weight="fill" /> Use in a post
                      </Button>
                      {item.kind === "image" && (
                        <div className="flex gap-2">
                          {canManageMedia && (
                            <Button data-testid="media-lightbox-rename-button" onClick={() => openRename(item)} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-9 text-xs rounded-[12px] gap-1">
                              <TextAa size={14} weight="regular" /> Rename
                            </Button>
                          )}
                          <Button data-testid="media-lightbox-copy-public-url-button" onClick={() => handleCopyPublicUrl(item)} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-9 text-xs rounded-[12px] gap-1">
                            <Copy size={14} weight="regular" /> Copy Email URL
                          </Button>
                        </div>
                      )}
                      {canManageMedia && item.kind !== "audio" && (
                        <div className="flex gap-2">
                          <Button data-testid="media-lightbox-edit-button" onClick={() => { setLightbox(null); setEditorItem(item); }} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-9 text-xs rounded-[12px] gap-1">
                            <PencilSimple size={14} weight="regular" /> Edit
                          </Button>
                          <Button data-testid="media-lightbox-frame-button" onClick={() => { setLightbox(null); setFrameItem(item); }} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-9 text-xs rounded-[12px] gap-1">
                            <Frame size={14} /> Frame
                          </Button>
                        </div>
                      )}
                      {canDeleteMedia && (
                        <Button data-testid="media-lightbox-delete-button" onClick={() => handleDelete(item.id)} className="w-full bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white h-9 text-xs rounded-[12px] gap-1">
                          <Trash size={14} /> Delete
                        </Button>
                      )}
                    </>
                  ) : (
                    canManageMedia && (
                      <div className="flex gap-2">
                        <Button data-testid="media-lightbox-edit-resubmit-button" onClick={() => { setLightbox(null); setEditorItem(item); }} className="flex-1 bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-9 text-xs rounded-[12px] gap-1">
                          <PencilSimple size={14} weight="regular" /> Edit & Resubmit
                        </Button>
                        {canDeleteMedia && (
                          <Button data-testid="media-lightbox-delete-rejected-button" onClick={() => handleDelete(item.id)} className="bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white h-9 text-xs rounded-[12px] px-3">
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
